import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useAuth } from "../context/AuthContext";

const FALLBACK_COVER =
  "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1800&q=85";

const ACTIVITIES = [
  ["hiking", "Planinarenje"],
  ["trekking", "Trekking"],
  ["camping", "Kampovanje"],
  ["cycling", "Biciklizam"],
  ["mountain biking", "MTB"],
  ["trail running", "Trail running"],
  ["climbing", "Penjanje"],
  ["via ferrata", "Via ferrata"],
  ["caving", "Speleologija"],
  ["canyoning", "Kanjoning"],
  ["rafting", "Rafting"],
  ["kayaking", "Kajak"],
  ["canoeing", "Kanu"],
  ["sup", "SUP"],
  ["sailing", "Jedrenje"],
  ["surfing", "Surfing"],
  ["kitesurfing", "Kitesurfing"],
  ["diving", "Ronjenje"],
  ["paragliding", "Paraglajding"],
  ["skydiving", "Padobranstvo"],
  ["skiing", "Skijanje"],
  ["snowboarding", "Snowboarding"],
  ["snowshoeing", "Krpljanje"],
  ["horse riding", "Jahanje"],
  ["fishing", "Ribolov"],
  ["off-road", "Off-road / 4x4"],
  ["nature trip", "Izlet u prirodi"],
  ["other", "Ostalo"],
];

const INCLUDED_PRESETS = [
  "Ručak",
  "Prevoz",
  "Vodič",
  "Oprema",
  "Noćenje",
  "Fotografisanje",
  "Osiguranje",
  "Piće / osveženje",
  "Ulaznice",
  "Kupanje",
];

function Icon({
  name,
  size = 20,
  strokeWidth = 2,
  fill = "none",
}) {
  const icons = {
    arrowLeft: (
      <>
        <path d="M19 12H5" />
        <path d="m11 18-6-6 6-6" />
      </>
    ),

    arrowRight: (
      <>
        <path d="M5 12h14" />
        <path d="m13 6 6 6-6 6" />
      </>
    ),

    compass: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m15.5 8.5-2 5-5 2 2-5 5-2Z" />
      </>
    ),

    calendar: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M16 3v4M8 3v4M3 10h18" />
      </>
    ),

    clock: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),

    mapPin: (
      <>
        <path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z" />
        <circle cx="12" cy="10" r="2.5" />
      </>
    ),

    globe: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18" />
        <path d="M12 3a15 15 0 0 1 0 18" />
        <path d="M12 3a15 15 0 0 0 0 18" />
      </>
    ),

    edit: (
      <>
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z" />
      </>
    ),

    image: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="m21 15-5-5L5 21" />
      </>
    ),

    upload: (
      <>
        <path d="M12 16V4" />
        <path d="m7 9 5-5 5 5" />
        <path d="M5 20h14" />
      </>
    ),

    users: (
      <>
        <circle cx="9" cy="8" r="3" />
        <path d="M3 20v-2a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v2" />
        <path d="M16 4.5a3 3 0 0 1 0 6" />
        <path d="M17 13a5 5 0 0 1 4 5v2" />
      </>
    ),

    euro: (
      <>
        <path d="M18 7.5a6 6 0 1 0 0 9" />
        <path d="M5 10h9M5 14h8" />
      </>
    ),

    check: <path d="m5 12 4 4L19 6" />,

    close: (
      <>
        <path d="m6 6 12 12" />
        <path d="m18 6-12 12" />
      </>
    ),

    alert: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v5" />
        <path d="M12 16h.01" />
      </>
    ),

    shield: (
      <>
        <path d="M12 3 5 6v5c0 4.6 2.9 8.4 7 10 4.1-1.6 7-5.4 7-10V6l-7-3Z" />
        <path d="m9 12 2 2 4-4" />
      </>
    ),

    info: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11v5" />
        <path d="M12 8h.01" />
      </>
    ),

    trash: (
      <>
        <path d="M3 6h18" />
        <path d="M8 6V4h8v2" />
        <path d="m19 6-1 15H6L5 6" />
      </>
    ),

    mountain: (
      <>
        <path d="m3 20 7-12 4 7 2-3 5 8" />
        <path d="m8.8 10 2.2 2 1.4-1.4" />
      </>
    ),

    sparkle: (
      <>
        <path d="m12 3 1.1 3.3L16 8l-2.9 1.7L12 13l-1.1-3.3L8 8l2.9-1.7L12 3Z" />
        <path d="m18 14 .7 2.3L21 17l-2.3.7L18 20l-.7-2.3L15 17l2.3-.7L18 14Z" />
      </>
    ),
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {icons[name]}
    </svg>
  );
}

function formatDate(value) {
  if (!value) return "Datum nije izabran";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Datum nije izabran";
  }

  return new Intl.DateTimeFormat("sr-Latn-RS", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatPrice(value) {
  const number = Number(value);

  if (!Number.isFinite(number) || number <= 0) {
    return "Besplatno";
  }

  return new Intl.NumberFormat("sr-Latn-RS", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(number);
}

function FormField({
  label,
  icon,
  type = "text",
  value,
  onChange,
  placeholder,
  required = false,
  min,
  max,
  step,
  hint,
}) {
  return (
    <label className="createEventField">
      <span className="createEventLabel">
        {label}
        {required && <strong>*</strong>}
      </span>

      <span className="createEventInputWrapper">
        <span className="createEventInputIcon">
          <Icon name={icon} size={17} />
        </span>

        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          min={min}
          max={max}
          step={step}
        />
      </span>

      {hint && <small className="createEventHint">{hint}</small>}
    </label>
  );
}

function LoadingState() {
  return (
    <>
      <CreateEventStyles />

      <main className="createEventStatePage">
        <div className="createEventStateCard">
          <span className="createEventLoader" />
          <h1>Pripremamo avanturu</h1>
          <p>Učitavamo tvoj host profil i podešavanja.</p>
        </div>
      </main>
    </>
  );
}

export default function CreateEvent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile, loading, isHost } = useAuth();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isAgentPrefill = searchParams.get("source") === "agent";

  const [title, setTitle] = useState(() => searchParams.get("title") || "");
  const [activities, setActivities] = useState(() => {
    const prefill = searchParams.get("activity");
    return prefill ? [prefill] : [];
  });
  const [includedItems, setIncludedItems] = useState([]);
  const [customIncludedItem, setCustomIncludedItem] = useState("");
  const [description, setDescription] = useState(() => searchParams.get("description") || "");
  const [location, setLocation] = useState(() => searchParams.get("location") || "");
  const [country, setCountry] = useState(() => searchParams.get("country") || "");
  const [price, setPrice] = useState(() => searchParams.get("price") || "");
  const [minParticipants, setMinParticipants] = useState(
    () => searchParams.get("min_participants") || ""
  );
  const [capacity, setCapacity] = useState(() => searchParams.get("capacity") || "");
  const [startDate, setStartDate] = useState(() => searchParams.get("start") || "");
  const [endDate, setEndDate] = useState(() => searchParams.get("end") || "");
  const [photoFiles, setPhotoFiles] = useState([]);
  const [coverIndex, setCoverIndex] = useState(0);

  const photoPreviews = useMemo(
    () => photoFiles.map((file) => URL.createObjectURL(file)),
    [photoFiles]
  );

  const coverPreview = photoPreviews[coverIndex] || FALLBACK_COVER;

  useEffect(() => {
    return () => {
      photoPreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [photoPreviews]);

  function toggleActivity(value) {
    setActivities((current) =>
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value]
    );
    if (error) setError("");
  }

  function toggleIncludedItem(item) {
    setIncludedItems((current) =>
      current.includes(item)
        ? current.filter((value) => value !== item)
        : [...current, item]
    );
    if (error) setError("");
  }

  function addCustomIncludedItem() {
    const clean = customIncludedItem.trim();
    if (!clean) return;

    if (!includedItems.some((item) => item.toLowerCase() === clean.toLowerCase())) {
      setIncludedItems((current) => [...current, clean]);
    }

    setCustomIncludedItem("");
    if (error) setError("");
  }

  function removeIncludedItem(item) {
    setIncludedItems((current) => current.filter((value) => value !== item));
  }

  function handlePhotoSelection(event) {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";

    if (!selected.length) return;

    const invalid = selected.find((file) => !file.type.startsWith("image/"));
    if (invalid) {
      setError("Možeš dodati samo fotografije.");
      return;
    }

    const tooLarge = selected.find((file) => file.size > 8 * 1024 * 1024);
    if (tooLarge) {
      setError("Svaka fotografija može imati najviše 8 MB.");
      return;
    }

    setPhotoFiles((current) => {
      const remaining = Math.max(8 - current.length, 0);
      if (remaining === 0) {
        setError("Možeš dodati najviše 8 fotografija.");
        return current;
      }

      if (selected.length > remaining) {
        setError(`Dodato je prvih ${remaining} fotografija. Maksimum je 8.`);
      }

      return [...current, ...selected.slice(0, remaining)];
    });
  }

  function removePhoto(index) {
    setPhotoFiles((current) => current.filter((_, itemIndex) => itemIndex !== index));
    setCoverIndex((currentCover) => {
      if (index < currentCover) return Math.max(currentCover - 1, 0);
      if (index === currentCover) return 0;
      return currentCover;
    });
  }

  const completion = useMemo(() => {
    const checks = [
      activities.length, title.trim(), description.trim(), location.trim(), country.trim(),
      minParticipants, capacity, photoFiles.length,
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }, [
    activities,
    title,
    description,
    location,
    country,
    minParticipants,
    capacity,
    photoFiles.length,
  ]);

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");

    if (loading || saving) return;

    if (!user || !profile) {
      setError("Moraš biti ulogovan kao domaćin.");
      return;
    }

    if (!isHost) {
      setError("Samo domaćin može da kreira avanturu.");
      return;
    }

    const cleanTitle = title.trim();
    const cleanDescription = description.trim();
    const cleanLocation = location.trim();
    const cleanCountry = country.trim();

    if (!activities.length) {
      setError("Izaberi najmanje jednu aktivnost avanture.");
      return;
    }

    if (!cleanTitle) {
      setError("Naziv avanture je obavezan.");
      return;
    }

    const minPeople = Number(minParticipants || 1);
    const maxPeople = Number(capacity || 1);

    if (!Number.isInteger(minPeople) || minPeople < 1) {
      setError("Minimalan broj osoba mora biti najmanje 1.");
      return;
    }

    if (!Number.isInteger(maxPeople) || maxPeople < 1) {
      setError("Maksimalan broj osoba mora biti najmanje 1.");
      return;
    }

    if (minPeople > maxPeople) {
      setError("Minimalan broj osoba ne može biti veći od maksimalnog.");
      return;
    }

    if (
      startDate &&
      endDate &&
      new Date(endDate).getTime() <=
        new Date(startDate).getTime()
    ) {
      setError(
        "Datum završetka mora biti posle datuma početka."
      );
      return;
    }

    if (!Number.isFinite(Number(price || 0)) || Number(price || 0) < 0) {
      setError("Cena mora biti 0 ili pozitivan broj.");
      return;
    }

    try {
      setSaving(true);

      const uploadedPaths = [];
      const uploadedUrls = [];

      try {
        for (let index = 0; index < photoFiles.length; index += 1) {
          const file = photoFiles[index];
          const safeName = (file.name || `photo-${index}.jpg`)
            .toLowerCase()
            .replace(/\s+/g, "-")
            .replace(/[^a-z0-9.-]/g, "");

          const fileName = `${profile.id}/${Date.now()}-${index}-${Math.random()
            .toString(36)
            .slice(2)}-${safeName}`;

          const { error: uploadError } = await supabase.storage
            .from("event-covers")
            .upload(fileName, file, {
              cacheControl: "3600",
              upsert: false,
              contentType: file.type || undefined,
            });

          if (uploadError) throw uploadError;

          const { data: publicUrlData } = supabase.storage
            .from("event-covers")
            .getPublicUrl(fileName);

          if (!publicUrlData?.publicUrl) {
            throw new Error("Nije moguće dobiti URL fotografije.");
          }

          uploadedPaths.push(fileName);
          uploadedUrls.push(publicUrlData.publicUrl);
        }

        const cover_url =
          uploadedUrls.length > 0
            ? uploadedUrls[Math.min(coverIndex, uploadedUrls.length - 1)]
            : null;

        const { data, error: insertError } = await supabase
          .from("events")
          .insert({
            host_id: profile.id,
            activities,
            title: cleanTitle,
            description: cleanDescription,
            location: cleanLocation,
            country: cleanCountry,
            cover_url,
            gallery_urls: uploadedUrls.filter(
              (_, index) => index !== Math.min(coverIndex, Math.max(uploadedUrls.length - 1, 0))
            ).slice(0, 7),
            included_items: includedItems,
            price: Number(price || 0),
            min_participants: minPeople,
            capacity: maxPeople,
            start_date: startDate || null,
            end_date: endDate || null,
            is_active: true,
          })
          .select("id")
          .single();

        if (insertError) throw insertError;

        navigate(`/event/${data.id}`);
      } catch (submitError) {
        if (uploadedPaths.length) {
          const { error: cleanupError } = await supabase.storage
            .from("event-covers")
            .remove(uploadedPaths);

          if (cleanupError) {
            console.error("Cleanup fotografija nije uspeo:", cleanupError);
          }
        }

        throw submitError;
      }
    } catch (err) {
      console.error("Greška pri kreiranju avanture:", err);

      setError(
        err.message || "Greška pri kreiranju avanture."
      );
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    navigate("/host-dashboard");
  }

  if (loading) {
    return <LoadingState />;
  }

  const displayLocation =
    [location.trim(), country.trim()]
      .filter(Boolean)
      .join(", ") || "Lokacija nije dodata";

  return (
    <>
      <CreateEventStyles />

      <main className="createEventPage">
        <div className="createEventShell">
          <aside className="eventPreviewPanel">
            <div className="eventPreviewImage">
              <img src={coverPreview} alt="" />

              <div className="eventPreviewOverlay" />

              <button
                type="button"
                className="previewBackButton"
                onClick={handleCancel}
              >
                <Icon name="arrowLeft" size={17} />
                Dashboard
              </button>

              <span className="previewBadge">
                <Icon name="calendar" size={15} />
                Nova avantura
              </span>

              <div className="previewImageBottom">
                <span>
                  <Icon name="clock" size={14} />
                  {startDate ? formatDate(startDate) : "Termin po dogovoru"}
                </span>

                <span>
                  <Icon name="users" size={14} />
                  {minParticipants && capacity
                    ? `${minParticipants}–${capacity} osoba`
                    : capacity
                    ? `Do ${capacity} osoba`
                    : "Broj osoba nije dodat"}
                </span>
              </div>
            </div>

            <div className="eventPreviewBody">
              <span className="previewKicker">
                Pregled avanture
              </span>

              <h2>{title.trim() || "Naziv tvoje avanture"}</h2>

              <div className="previewLocation">
                <Icon name="mapPin" size={15} />
                {displayLocation}
              </div>

              <p className="previewDescription">
                {description.trim() ||
                  "Opis avanture će se prikazati ovde. Dodaj informacije o planu, mestu okupljanja, trajanju i opremi koju učesnici treba da ponesu."}
              </p>

              <div className="previewDetails">
                <article>
                  <span>
                    <Icon name="calendar" size={17} />
                  </span>

                  <div>
                    <small>Početak</small>
                    <strong>{startDate ? formatDate(startDate) : "Termin po dogovoru"}</strong>
                  </div>
                </article>

                <article>
                  <span>
                    <Icon name="clock" size={17} />
                  </span>

                  <div>
                    <small>Završetak</small>
                    <strong>{endDate ? formatDate(endDate) : "Po dogovoru"}</strong>
                  </div>
                </article>

                <article>
                  <span>
                    <Icon name="euro" size={17} />
                  </span>

                  <div>
                    <small>Cena</small>
                    <strong>{formatPrice(price)}</strong>
                  </div>
                </article>

                <article>
                  <span>
                    <Icon name="users" size={17} />
                  </span>

                  <div>
                    <small>Minimum</small>
                    <strong>
                      {minParticipants
                        ? `${minParticipants} osoba`
                        : "Nije navedeno"}
                    </strong>
                  </div>
                </article>

                <article>
                  <span>
                    <Icon name="users" size={17} />
                  </span>

                  <div>
                    <small>Maksimum</small>
                    <strong>
                      {capacity
                        ? `${capacity} osoba`
                        : "Nije navedeno"}
                    </strong>
                  </div>
                </article>
              </div>

              <div className="previewHost">
                <span>
                  <Icon name="shield" size={18} />
                </span>

                <div>
                  <small>Organizator</small>
                  <strong>
                    {profile?.full_name ||
                      profile?.username ||
                      "MeetOutdoors domaćin"}
                  </strong>
                </div>
              </div>

              <div className="previewNotice">
                <Icon name="info" size={17} />

                <p>
                  Ovo je približan pregled kartice avanture.
                  Objavljena stranica može sadržati dodatne
                  informacije.
                </p>
              </div>
            </div>
          </aside>

          <section className="createEventContent">
            <header className="createEventHeader">
              <div>
                <button
                  type="button"
                  className="mobileBackButton"
                  onClick={handleCancel}
                >
                  <Icon name="arrowLeft" size={17} />
                </button>

                <span className="createEventBrand">
                  <span>
                    <Icon name="compass" size={21} />
                  </span>

                  MeetOutdoors
                </span>

                <span className="createEventKicker">
                  Host alat
                </span>

                <h1>Kreiraj avanturu.</h1>

                <p>
                  Objavi svoju outdoor ponudu u MeetOutdoors katalogu.
                  Dodaj jasne detalje, cenu i uslove — korisnik zatim
                  kontaktira domaćina direktno.
                </p>
              </div>

              <span className="draftBadge">
                <Icon name={isAgentPrefill ? "sparkle" : "edit"} size={16} />
                {isAgentPrefill ? "Agent nacrt" : "Novi nacrt"}
              </span>
            </header>

            {isAgentPrefill && (
              <section className="eventAgentBanner" aria-label="MeetOutdoors Agent predlog">
                <span className="eventAgentIcon"><Icon name="sparkle" size={19} /></span>
                <div>
                  <small>MeetOutdoors Intelligence</small>
                  <strong>Agent je pripremio nacrt iz realne potražnje.</strong>
                  <p>Predloženi naslov, lokacija, broj osoba i cena služe kao polazna tačka. Ti imaš potpunu kontrolu pre objave.</p>
                </div>
                <span className="eventAgentBadge"><Icon name="check" size={14} /> Predlog, ne automatika</span>
              </section>
            )}

            <section className="eventCompletion">
              <div className="eventCompletionTop"><span>Spremnost avanture</span><strong>{completion}%</strong></div>
              <div className="eventCompletionTrack"><span style={{ width: `${completion}%` }} /></div>
              <p>{completion >= 75 ? "Avantura je skoro spremna — proveri detalje i objavi." : "Dodaj lokaciju, vreme i vizuelni identitet da korisnik odmah razume ponudu."}</p>
            </section>

            <form
              onSubmit={handleSubmit}
              className="createEventForm"
            >
              <section className="eventFormSection">
                <div className="eventFormHeading">
                  <span>
                    <Icon name="sparkle" size={20} />
                  </span>

                  <div>
                    <small>Osnovne informacije</small>
                    <h2>Predstavi avanturu</h2>
                    <p>
                      Naziv i opis treba jasno da objasne šta učesnici mogu da očekuju od avanture.
                    </p>
                  </div>
                </div>

                <div className="eventActivityPicker">
                  <div className="eventActivityPickerTop">
                    <span>Aktivnosti *</span>
                    <small>Izaberi jednu ili više aktivnosti</small>
                  </div>

                  <div className="eventActivityGrid">
                    {ACTIVITIES.map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        className={activities.includes(value) ? "active" : ""}
                        aria-pressed={activities.includes(value)}
                        onClick={() => toggleActivity(value)}
                      >
                        <span className="eventActivityDot" />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <FormField
                  label="Naziv avanture"
                  icon="mountain"
                  value={title}
                  onChange={(event) => {
                    setTitle(event.target.value);
                    if (error) setError("");
                  }}
                  placeholder="Na primer: Planinarenje uz zalazak sunca"
                  required
                  hint="Koristi jasan naziv koji opisuje aktivnost."
                />

                <label className="createEventField eventDescriptionField">
                  <span className="createEventLabel">
                    Opis avanture
                  </span>

                  <span className="createEventTextareaWrapper">
                    <span>
                      <Icon name="edit" size={17} />
                    </span>

                    <textarea
                      rows="7"
                      maxLength="2000"
                      value={description}
                      onChange={(event) =>
                        setDescription(event.target.value)
                      }
                      placeholder="Opiši avanturu, tok iskustva, mesto okupljanja, pravila i opremu koju učesnici treba da ponesu..."
                    />
                  </span>

                  <span className="descriptionCount">
                    {description.length}/2000
                  </span>
                </label>
              </section>

              <section className="eventFormSection">
                <div className="eventFormHeading">
                  <span>
                    <Icon name="check" size={20} />
                  </span>

                  <div>
                    <small>Šta je uključeno? · opciono</small>
                    <h2>Istakni šta je uključeno u ponudu</h2>
                    <p>
                      Izaberi gotove stavke ili dodaj svoju, na primer „Kupanje na vodopadu“.
                    </p>
                  </div>
                </div>

                <div className="eventIncludedPresetGrid">
                  {INCLUDED_PRESETS.map((item) => (
                    <button
                      key={item}
                      type="button"
                      className={includedItems.includes(item) ? "active" : ""}
                      aria-pressed={includedItems.includes(item)}
                      onClick={() => toggleIncludedItem(item)}
                    >
                      <span>✓</span>
                      {item}
                    </button>
                  ))}
                </div>

                <div className="eventIncludedCustomRow">
                  <input
                    type="text"
                    value={customIncludedItem}
                    maxLength={80}
                    onChange={(event) => setCustomIncludedItem(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        addCustomIncludedItem();
                      }
                    }}
                    placeholder="Dodaj svoju stavku, npr. Kupanje na vodopadu"
                  />
                  <button type="button" onClick={addCustomIncludedItem}>
                    Dodaj
                  </button>
                </div>

                {includedItems.length > 0 && (
                  <div className="eventIncludedSelected">
                    {includedItems.map((item) => (
                      <span key={item}>
                        ✓ {item}
                        <button
                          type="button"
                          onClick={() => removeIncludedItem(item)}
                          aria-label={`Ukloni ${item}`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </section>

              <section className="eventFormSection">
                <div className="eventFormHeading">
                  <span>
                    <Icon name="mapPin" size={20} />
                  </span>

                  <div>
                    <small>Lokacija</small>
                    <h2>Gde se avantura održava?</h2>
                    <p>
                      Dodaj mesto okupljanja ili naziv područja
                      u kome se avantura održava.
                    </p>
                  </div>
                </div>

                <div className="eventFieldsGrid">
                  <FormField
                    label="Lokacija"
                    icon="mapPin"
                    value={location}
                    onChange={(event) =>
                      setLocation(event.target.value)
                    }
                    placeholder="Na primer: Niš, Čair"
                  />

                  <FormField
                    label="Država"
                    icon="globe"
                    value={country}
                    onChange={(event) =>
                      setCountry(event.target.value)
                    }
                    placeholder="Na primer: Srbija"
                  />
                </div>
              </section>

              <section className="eventFormSection">
                <div className="eventFormHeading">
                  <span>
                    <Icon name="calendar" size={20} />
                  </span>

                  <div>
                    <small>Termin i trajanje · opciono</small>
                    <h2>Termin avanture</h2>
                    <p>
                      Ako termin još nije poznat, ostavi oba polja prazna — pisaće „Termin po dogovoru“. Za višednevnu avanturu unesi početak i završetak.
                    </p>
                  </div>
                </div>

                <div
                  style={{
                    marginBottom: 14,
                    padding: "12px 14px",
                    borderRadius: 14,
                    background: "rgba(45, 91, 61, 0.07)",
                    border: "1px solid rgba(45, 91, 61, 0.12)",
                    fontSize: 13,
                    lineHeight: 1.55,
                    color: "#496057",
                  }}
                >
                  <strong style={{ color: "#20382c" }}>Kako radi termin:</strong>{" "}
                  jedan datum = jednodnevna avantura · početak + završetak = višednevna avantura · bez datuma = termin po dogovoru.
                </div>

                <div className="eventFieldsGrid">
                  <FormField
                    label="Početak avanture"
                    icon="calendar"
                    type="datetime-local"
                    value={startDate}
                    onChange={(event) =>
                      setStartDate(event.target.value)
                    }
                  />

                  <FormField
                    label="Završetak avanture"
                    icon="clock"
                    type="datetime-local"
                    value={endDate}
                    onChange={(event) =>
                      setEndDate(event.target.value)
                    }
                    min={startDate || undefined}
                  />
                </div>
              </section>

              <section className="eventFormSection">
                <div className="eventFormHeading">
                  <span>
                    <Icon name="users" size={20} />
                  </span>

                  <div>
                    <small>Uslovi ponude</small>
                    <h2>Cena i broj osoba</h2>
                    <p>
                      Unesi minimalan broj osoba potreban za realizaciju i
                      maksimalan broj osoba koje domaćin može da primi.
                    </p>
                  </div>
                </div>

                <div className="eventFieldsGrid">
                  <FormField
                    label="Cena po osobi"
                    icon="euro"
                    type="number"
                    min="0"
                    step="0.01"
                    value={price}
                    onChange={(event) =>
                      setPrice(event.target.value)
                    }
                    placeholder="0"
                    hint="Cena se čuva u evrima."
                  />

                  <FormField
                    label="Minimalan broj osoba"
                    icon="users"
                    type="number"
                    min="1"
                    max={capacity || undefined}
                    step="1"
                    value={minParticipants}
                    onChange={(event) => {
                      setMinParticipants(event.target.value);
                      if (error) setError("");
                    }}
                    placeholder="6"
                    hint="Najmanji broj osoba potreban da bi se avantura realizovala."
                    required
                  />

                  <FormField
                    label="Maksimalan broj osoba"
                    icon="users"
                    type="number"
                    min={minParticipants || "1"}
                    step="1"
                    value={capacity}
                    onChange={(event) => {
                      setCapacity(event.target.value);
                      if (error) setError("");
                    }}
                    placeholder="15"
                    hint="Najveći broj osoba koje možeš da primiš."
                    required
                  />
                </div>
              </section>

              <section className="eventFormSection">
                <div className="eventFormHeading">
                  <span>
                    <Icon name="image" size={20} />
                  </span>

                  <div>
                    <small>Galerija avanture · opciono</small>
                    <h2>Dodaj do 8 fotografija</h2>
                    <p>
                      Na telefonu možeš izabrati više fotografija direktno iz galerije.
                      Označi jednu kao naslovnu.
                    </p>
                  </div>
                </div>

                <label className={photoFiles.length ? "eventUpload selected" : "eventUpload"}>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoSelection}
                  />

                  <span className="eventUploadIcon">
                    <Icon name={photoFiles.length ? "check" : "upload"} size={22} />
                  </span>

                  <span className="eventUploadCopy">
                    <strong>
                      {photoFiles.length
                        ? `${photoFiles.length} / 8 fotografija`
                        : "Izaberi fotografije"}
                    </strong>
                    <small>
                      JPG, PNG ili WEBP · do 8 MB po fotografiji · maksimalno 8.
                    </small>
                  </span>

                  <span className="eventUploadAction">
                    {photoFiles.length ? "Dodaj još" : "Izaberi"}
                  </span>
                </label>

                {photoFiles.length > 0 && (
                  <div className="eventPhotoGrid">
                    {photoPreviews.map((preview, index) => (
                      <article
                        key={`${photoFiles[index]?.name}-${index}`}
                        className={coverIndex === index ? "eventPhotoItem cover" : "eventPhotoItem"}
                      >
                        <img src={preview} alt={`Fotografija ${index + 1}`} />
                        {coverIndex === index && (
                          <span className="eventPhotoCoverBadge">Naslovna</span>
                        )}
                        <div className="eventPhotoActions">
                          {coverIndex !== index && (
                            <button type="button" onClick={() => setCoverIndex(index)}>
                              Postavi naslovnu
                            </button>
                          )}
                          <button
                            type="button"
                            className="danger"
                            onClick={() => removePhoto(index)}
                            aria-label={`Ukloni fotografiju ${index + 1}`}
                          >
                            <Icon name="trash" size={14} />
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>

              {error && (
                <div
                  className="createEventError"
                  role="alert"
                >
                  <span>
                    <Icon name="alert" size={18} />
                  </span>

                  <p>{error}</p>

                  <button
                    type="button"
                    onClick={() => setError("")}
                    aria-label="Zatvori poruku"
                  >
                    <Icon name="close" size={16} />
                  </button>
                </div>
              )}

              <div className="createEventActions">
                <button
                  type="button"
                  className="cancelEventButton"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Otkaži
                </button>

                <button
                  type="submit"
                  className="publishEventButton"
                  disabled={saving || !activities.length || !title.trim()}
                >
                  {saving ? (
                    <>
                      <span className="publishLoader" />
                      Kreiranje avanture...
                    </>
                  ) : (
                    <>
                      Objavi avanturu
                      <Icon name="arrowRight" size={17} />
                    </>
                  )}
                </button>
              </div>

              <div className="eventSecurityNotice">
                <Icon name="shield" size={17} />

                <p>
                  Ponuda će odmah biti vidljiva korisnicima nakon objavljivanja.
                  MeetOutdoors prikazuje tvoju ponudu — korisnik
                  kontaktira domaćina direktno.
                </p>
              </div>
            </form>
          </section>
        </div>
      </main>
    </>
  );
}

function CreateEventStyles() {
  return (
    <style>{`
      * {
        box-sizing: border-box;
      }

      body {
        margin: 0;
        background: #f1f3ec;
      }

      button,
      input,
      textarea {
        font: inherit;
      }

      button,
      label,
      a {
        -webkit-tap-highlight-color: transparent;
      }

      .createEventPage,
      .createEventStatePage {
        min-height: 100vh;
        color: #17271f;
        font-family:
          Inter,
          ui-sans-serif,
          system-ui,
          -apple-system,
          BlinkMacSystemFont,
          "Segoe UI",
          sans-serif;
      }

      .createEventPage {
        padding: 28px;
        background:
          radial-gradient(
            circle at 5% 0%,
            rgba(169, 203, 131, 0.18),
            transparent 25%
          ),
          radial-gradient(
            circle at 97% 30%,
            rgba(85, 129, 91, 0.1),
            transparent 24%
          ),
          #f1f3ec;
      }

      .createEventShell {
        width: min(1280px, 100%);
        min-height: calc(100vh - 56px);
        display: grid;
        grid-template-columns:
          minmax(340px, 0.75fr)
          minmax(0, 1.25fr);
        margin: 0 auto;
        overflow: hidden;
        border: 1px solid rgba(34, 55, 43, 0.1);
        border-radius: 34px;
        background: rgba(255, 255, 255, 0.82);
        box-shadow: 0 28px 85px rgba(27, 49, 35, 0.11);
      }

      .eventPreviewPanel {
        min-width: 0;
        background: #102b1c;
        color: white;
      }

      .eventPreviewImage {
        position: relative;
        height: 390px;
        overflow: hidden;
      }

      .eventPreviewImage > img {
        width: 100%;
        height: 100%;
        display: block;
        object-fit: cover;
      }

      .eventPreviewOverlay {
        position: absolute;
        inset: 0;
        background:
          linear-gradient(
            180deg,
            rgba(5, 17, 10, 0.2),
            rgba(5, 17, 10, 0.12) 35%,
            rgba(7, 24, 14, 0.94)
          );
      }

      .previewBackButton,
      .previewBadge {
        position: absolute;
        top: 22px;
        display: inline-flex;
        align-items: center;
        gap: 7px;
        min-height: 40px;
        padding: 0 12px;
        border: 1px solid rgba(255, 255, 255, 0.18);
        border-radius: 13px;
        background: rgba(255, 255, 255, 0.1);
        color: white;
        font-size: 9px;
        font-weight: 850;
        backdrop-filter: blur(13px);
      }

      .previewBackButton {
        left: 22px;
        cursor: pointer;
      }

      .previewBackButton:hover {
        background: rgba(255, 255, 255, 0.18);
      }

      .previewBadge {
        right: 22px;
        color: #d8f7aa;
      }

      .previewImageBottom {
        position: absolute;
        right: 22px;
        bottom: 22px;
        left: 22px;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }

      .previewImageBottom > span {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        min-height: 31px;
        padding: 0 10px;
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 999px;
        background: rgba(5, 20, 11, 0.5);
        color: rgba(255, 255, 255, 0.84);
        font-size: 8px;
        font-weight: 800;
        backdrop-filter: blur(11px);
      }

      .eventPreviewBody {
        padding: 28px 28px 36px;
      }

      .previewKicker {
        display: block;
        color: #c9f28c;
        font-size: 9px;
        font-weight: 900;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }

      .eventPreviewBody h2 {
        margin: 10px 0 0;
        color: white;
        font-size: clamp(32px, 4vw, 48px);
        line-height: 1;
        letter-spacing: -0.055em;
      }

      .previewLocation {
        display: flex;
        align-items: center;
        gap: 7px;
        margin-top: 15px;
        color: rgba(255, 255, 255, 0.68);
        font-size: 10px;
        font-weight: 750;
      }

      .previewLocation svg {
        color: #c9f28c;
      }

      .previewDescription {
        margin: 18px 0 0;
        color: rgba(255, 255, 255, 0.59);
        font-size: 10px;
        line-height: 1.72;
        white-space: pre-line;
      }

      .previewDetails {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 9px;
        margin-top: 22px;
      }

      .previewDetails article {
        display: flex;
        align-items: center;
        gap: 9px;
        min-width: 0;
        padding: 12px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 14px;
        background: rgba(255, 255, 255, 0.06);
      }

      .previewDetails article > span {
        display: grid;
        place-items: center;
        flex: 0 0 auto;
        width: 34px;
        height: 34px;
        border-radius: 11px;
        background: rgba(201, 242, 140, 0.09);
        color: #c9f28c;
      }

      .previewDetails small,
      .previewDetails strong {
        display: block;
      }

      .previewDetails small {
        color: rgba(255, 255, 255, 0.4);
        font-size: 7px;
      }

      .previewDetails strong {
        overflow: hidden;
        margin-top: 3px;
        color: rgba(255, 255, 255, 0.8);
        font-size: 8px;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .previewHost {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-top: 20px;
        padding-top: 18px;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
      }

      .previewHost > span {
        display: grid;
        place-items: center;
        width: 39px;
        height: 39px;
        border-radius: 12px;
        background: rgba(201, 242, 140, 0.1);
        color: #c9f28c;
      }

      .previewHost small,
      .previewHost strong {
        display: block;
      }

      .previewHost small {
        color: rgba(255, 255, 255, 0.39);
        font-size: 7px;
      }

      .previewHost strong {
        margin-top: 3px;
        color: rgba(255, 255, 255, 0.82);
        font-size: 9px;
      }

      .previewNotice {
        display: flex;
        align-items: flex-start;
        gap: 9px;
        margin-top: 20px;
        padding: 13px;
        border: 1px solid rgba(201, 242, 140, 0.14);
        border-radius: 14px;
        background: rgba(201, 242, 140, 0.06);
        color: #c9f28c;
      }

      .previewNotice svg {
        flex: 0 0 auto;
      }

      .previewNotice p {
        margin: 0;
        color: rgba(255, 255, 255, 0.48);
        font-size: 8px;
        line-height: 1.55;
      }

      .createEventContent {
        min-width: 0;
        padding: 39px;
        background:
          radial-gradient(
            circle at 100% 0%,
            rgba(186, 211, 155, 0.11),
            transparent 26%
          ),
          #fafbf7;
      }

      .createEventHeader {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 25px;
        margin-bottom: 30px;
      }

      .createEventBrand {
        display: inline-flex;
        align-items: center;
        gap: 9px;
        margin-bottom: 36px;
        color: #263c2f;
        font-size: 14px;
        font-weight: 900;
        letter-spacing: -0.025em;
      }

      .createEventBrand > span {
        display: grid;
        place-items: center;
        width: 39px;
        height: 39px;
        border-radius: 13px;
        background: #183a27;
        color: #c9f28c;
      }

      .createEventKicker {
        display: block;
        color: #779556;
        font-size: 9px;
        font-weight: 900;
        letter-spacing: 0.13em;
        text-transform: uppercase;
      }

      .createEventHeader h1 {
        margin: 10px 0 0;
        color: #20342a;
        font-size: clamp(43px, 5vw, 66px);
        line-height: 0.94;
        letter-spacing: -0.07em;
      }

      .createEventHeader p {
        max-width: 620px;
        margin: 17px 0 0;
        color: #7b877f;
        font-size: 12px;
        line-height: 1.65;
      }

      .draftBadge {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        flex: 0 0 auto;
        min-height: 39px;
        padding: 0 12px;
        border: 1px solid #d7e1d2;
        border-radius: 12px;
        background: #f1f6eb;
        color: #597244;
        font-size: 9px;
        font-weight: 850;
      }

      .mobileBackButton {
        display: none;
      }

      .createEventForm {
        display: grid;
        gap: 20px;
      }

      .eventFormSection {
        padding: 25px;
        border: 1px solid #dce4d9;
        border-radius: 25px;
        background: rgba(255, 255, 255, 0.8);
        box-shadow: 0 12px 34px rgba(31, 51, 38, 0.045);
      }

      .eventFormHeading {
        display: flex;
        align-items: flex-start;
        gap: 13px;
        margin-bottom: 23px;
      }

      .eventFormHeading > span {
        display: grid;
        place-items: center;
        flex: 0 0 auto;
        width: 43px;
        height: 43px;
        border-radius: 14px;
        background: #e7f0dc;
        color: #5d7a43;
      }

      .eventFormHeading small {
        display: block;
        color: #7f9d5c;
        font-size: 8px;
        font-weight: 900;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }

      .eventFormHeading h2 {
        margin: 6px 0 0;
        color: #2b4033;
        font-size: 22px;
        line-height: 1.05;
        letter-spacing: -0.04em;
      }

      .eventFormHeading p {
        margin: 7px 0 0;
        color: #89938c;
        font-size: 9px;
        line-height: 1.55;
      }

      .eventFieldsGrid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 15px;
      }

      .createEventField {
        display: grid;
        gap: 8px;
        min-width: 0;
      }

      .createEventLabel {
        color: #495c50;
        font-size: 9px;
        font-weight: 850;
      }

      .createEventLabel strong {
        margin-left: 3px;
        color: #9e453c;
      }

      .createEventInputWrapper,
      .createEventTextareaWrapper {
        position: relative;
        display: flex;
        align-items: center;
        min-width: 0;
      }

      .createEventInputIcon,
      .createEventTextareaWrapper > span {
        position: absolute;
        left: 14px;
        z-index: 1;
        display: grid;
        place-items: center;
        color: #829078;
        pointer-events: none;
      }

      .createEventTextareaWrapper > span {
        top: 15px;
      }

      .createEventInputWrapper input,
      .createEventTextareaWrapper textarea {
        width: 100%;
        border: 1px solid #d9e1d6;
        outline: none;
        background: #f8faf6;
        color: #25382d;
        transition: 0.18s ease;
      }

      .createEventInputWrapper input {
        min-height: 51px;
        padding: 0 14px 0 43px;
        border-radius: 14px;
        font-size: 11px;
      }

      .createEventTextareaWrapper textarea {
        min-height: 165px;
        resize: vertical;
        padding: 14px 14px 14px 43px;
        border-radius: 15px;
        font-size: 11px;
        line-height: 1.65;
      }

      .createEventInputWrapper input:focus,
      .createEventTextareaWrapper textarea:focus {
        border-color: #86a36b;
        background: white;
        box-shadow: 0 0 0 4px rgba(134, 163, 107, 0.1);
      }

      .createEventInputWrapper input::placeholder,
      .createEventTextareaWrapper textarea::placeholder {
        color: #a2aaa4;
      }

      .createEventHint {
        color: #959e97;
        font-size: 8px;
        line-height: 1.5;
      }

      .eventDescriptionField {
        position: relative;
      }

      .descriptionCount {
        position: absolute;
        right: 12px;
        bottom: 10px;
        padding: 4px 7px;
        border-radius: 8px;
        background: rgba(248, 250, 246, 0.9);
        color: #939d95;
        font-size: 7px;
      }

      .eventUpload {
        display: flex;
        align-items: center;
        gap: 13px;
        min-height: 105px;
        padding: 15px;
        border: 1px dashed #bdc9b8;
        border-radius: 17px;
        background: #f9faf7;
        cursor: pointer;
        transition: 0.18s ease;
      }

      .eventUpload:hover {
        border-color: #789a59;
        background: white;
      }

      .eventUpload.selected {
        border-style: solid;
        border-color: #97af83;
        background: #f0f6e9;
      }

      .eventUpload input {
        position: absolute;
        width: 1px;
        height: 1px;
        opacity: 0;
        pointer-events: none;
      }

      .eventUploadIcon {
        display: grid;
        place-items: center;
        flex: 0 0 auto;
        width: 52px;
        height: 52px;
        border-radius: 16px;
        background: #e7f0dc;
        color: #5c7842;
      }

      .eventUpload.selected .eventUploadIcon {
        background: #dceacd;
        color: #4c6c34;
      }

      .eventUploadCopy {
        min-width: 0;
        flex: 1;
      }

      .eventUploadCopy strong,
      .eventUploadCopy small {
        display: block;
      }

      .eventUploadCopy strong {
        overflow: hidden;
        color: #415448;
        font-size: 10px;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .eventUploadCopy small {
        margin-top: 5px;
        color: #929b94;
        font-size: 8px;
        line-height: 1.5;
      }

      .eventUploadAction {
        flex: 0 0 auto;
        padding: 9px 11px;
        border: 1px solid #d6dfd2;
        border-radius: 10px;
        background: white;
        color: #53665a;
        font-size: 8px;
        font-weight: 850;
      }

      .removeEventCover {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-top: 10px;
        padding: 8px 10px;
        border: 0;
        border-radius: 9px;
        background: #fff0ee;
        color: #9a463c;
        cursor: pointer;
        font-size: 8px;
        font-weight: 800;
      }

      .createEventError {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr) auto;
        align-items: center;
        gap: 11px;
        padding: 14px;
        border: 1px solid #efc7c2;
        border-radius: 16px;
        background: #fff0ee;
        color: #963f35;
      }

      .createEventError > span {
        display: grid;
        place-items: center;
        width: 33px;
        height: 33px;
        border-radius: 10px;
        background: #f8d7d3;
      }

      .createEventError p {
        margin: 0;
        font-size: 10px;
        line-height: 1.5;
      }

      .createEventError button {
        display: grid;
        place-items: center;
        width: 30px;
        height: 30px;
        padding: 0;
        border: 0;
        border-radius: 9px;
        background: transparent;
        color: inherit;
        cursor: pointer;
      }

      .createEventActions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 10px;
      }

      .cancelEventButton,
      .publishEventButton {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        min-height: 48px;
        padding: 0 18px;
        border-radius: 14px;
        cursor: pointer;
        font-size: 10px;
        font-weight: 900;
        transition: 0.18s ease;
      }

      .cancelEventButton {
        border: 1px solid #d6dfd3;
        background: white;
        color: #59685f;
      }

      .cancelEventButton:hover:not(:disabled) {
        border-color: #a3b09d;
      }

      .publishEventButton {
        min-width: 190px;
        border: 1px solid #183a27;
        background: #183a27;
        color: white;
        box-shadow: 0 12px 27px rgba(24, 58, 39, 0.17);
      }

      .publishEventButton:hover:not(:disabled) {
        gap: 12px;
        background: #234d35;
        transform: translateY(-2px);
      }

      .cancelEventButton:disabled,
      .publishEventButton:disabled {
        cursor: not-allowed;
        opacity: 0.65;
      }

      .publishLoader {
        width: 15px;
        height: 15px;
        border: 2px solid rgba(255, 255, 255, 0.25);
        border-top-color: white;
        border-radius: 50%;
        animation: createEventSpin 0.75s linear infinite;
      }

      .eventSecurityNotice {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        color: #89938c;
        font-size: 8px;
        text-align: center;
      }

      .eventSecurityNotice svg {
        color: #779357;
      }

      .eventSecurityNotice p {
        margin: 0;
      }

      .createEventStatePage {
        display: grid;
        place-items: center;
        padding: 24px;
        background:
          radial-gradient(
            circle at top left,
            rgba(166, 203, 126, 0.18),
            transparent 30%
          ),
          #f1f3ec;
      }

      .createEventStateCard {
        display: grid;
        place-items: center;
        width: min(500px, 100%);
        padding: 50px 30px;
        border: 1px solid #dce3d9;
        border-radius: 28px;
        background: rgba(255, 255, 255, 0.82);
        text-align: center;
        box-shadow: 0 20px 60px rgba(28, 48, 35, 0.08);
      }

      .createEventLoader {
        width: 37px;
        height: 37px;
        border: 3px solid #dce5d7;
        border-top-color: #52783c;
        border-radius: 50%;
        animation: createEventSpin 0.8s linear infinite;
      }

      @keyframes createEventSpin {
        to {
          transform: rotate(360deg);
        }
      }

      .createEventStateCard h1 {
        margin: 18px 0 0;
        color: #24372c;
        font-size: 28px;
        letter-spacing: -0.04em;
      }

      .createEventStateCard p {
        margin: 9px 0 0;
        color: #7e8981;
        font-size: 11px;
      }

      @media (max-width: 1000px) {
        .createEventShell {
          grid-template-columns:
            minmax(310px, 0.68fr)
            minmax(0, 1.32fr);
        }

        .previewDetails {
          grid-template-columns: 1fr;
        }
      }

      @media (max-width: 850px) {
        .createEventPage {
          padding: 18px;
        }

        .createEventShell {
          display: block;
          min-height: auto;
        }

        .eventPreviewImage {
          height: 360px;
        }

        .previewDetails {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
      }

      @media (max-width: 680px) {
        .createEventPage {
          padding: 0;
        }

        .createEventShell {
          border: 0;
          border-radius: 0;
        }

        .eventPreviewPanel {
          display: none;
        }

        .createEventContent {
          padding: 27px 20px 45px;
        }

        .createEventHeader {
          align-items: flex-start;
          flex-direction: column;
        }

        .mobileBackButton {
          display: grid;
          place-items: center;
          width: 39px;
          height: 39px;
          margin-bottom: 20px;
          padding: 0;
          border: 1px solid #d7dfd4;
          border-radius: 12px;
          background: white;
          color: #405449;
          cursor: pointer;
        }

        .createEventBrand {
          margin-bottom: 28px;
        }

        .eventFieldsGrid {
          grid-template-columns: 1fr;
        }
      }

      @media (max-width: 460px) {
        .createEventContent {
          padding: 24px 14px 40px;
        }

        .createEventHeader h1 {
          font-size: 43px;
        }

        .eventFormSection {
          padding: 19px;
          border-radius: 21px;
        }

        .eventUpload {
          align-items: flex-start;
          flex-wrap: wrap;
        }

        .eventUploadCopy {
          width: calc(100% - 68px);
          flex: none;
        }

        .eventUploadAction {
          margin-left: 65px;
        }

        .createEventActions {
          align-items: stretch;
          flex-direction: column-reverse;
        }

        .cancelEventButton,
        .publishEventButton {
          width: 100%;
        }

        .eventSecurityNotice {
          align-items: flex-start;
          text-align: left;
        }
      }


      /* =========================================================
         CREATE EVENT — ULTRA COMPACT UX
         Same direction as CreatePackage: less scroll, denser UI.
         ========================================================= */

      .createEventPage{padding:16px}
      .createEventShell{
        width:min(1380px,100%);
        min-height:calc(100vh - 32px);
        grid-template-columns:minmax(300px,.68fr) minmax(0,1.32fr);
        border-radius:24px;
      }

      .eventPreviewPanel{
        position:sticky;
        top:16px;
        align-self:start;
        max-height:calc(100vh - 32px);
        overflow:auto;
      }

      .eventPreviewImage{height:245px}
      .previewBackButton,.previewBadge{
        top:12px;
        min-height:32px;
        padding:0 9px;
        border-radius:10px;
        font-size:7px;
      }
      .previewBackButton{left:12px}
      .previewBadge{right:12px}
      .previewImageBottom{right:12px;bottom:12px;left:12px;gap:5px}
      .previewImageBottom>span{
        min-height:25px;
        padding:0 7px;
        font-size:6px;
      }

      .eventPreviewBody{padding:16px}
      .previewKicker{font-size:6px}
      .eventPreviewBody h2{
        margin-top:6px;
        font-size:clamp(24px,3vw,34px);
      }
      .previewLocation{margin-top:8px;font-size:8px}
      .previewDescription{
        margin-top:9px;
        font-size:8px;
        line-height:1.45;
        display:-webkit-box;
        -webkit-line-clamp:4;
        -webkit-box-orient:vertical;
        overflow:hidden;
      }
      .previewDetails{
        gap:5px;
        margin-top:10px;
      }
      .previewDetails article{
        gap:6px;
        padding:7px;
        border-radius:10px;
      }
      .previewDetails article>span{
        width:27px;
        height:27px;
        border-radius:8px;
      }
      .previewDetails small{font-size:5.5px}
      .previewDetails strong{font-size:7px}
      .previewHost{
        gap:7px;
        margin-top:10px;
        padding-top:9px;
      }
      .previewHost>span{
        width:31px;
        height:31px;
        border-radius:9px;
      }
      .previewHost small{font-size:5.5px}
      .previewHost strong{font-size:7.5px}
      .previewNotice{
        gap:6px;
        margin-top:9px;
        padding:8px;
        border-radius:10px;
      }
      .previewNotice p{font-size:6.5px;line-height:1.35}

      .createEventContent{padding:20px 22px}
      .createEventHeader{
        gap:12px;
        margin-bottom:12px;
      }
      .createEventBrand{
        gap:6px;
        margin-bottom:12px;
        font-size:10px;
      }
      .createEventBrand>span{
        width:31px;
        height:31px;
        border-radius:10px;
      }
      .createEventKicker{font-size:6.5px}
      .createEventHeader h1{
        margin-top:5px;
        font-size:clamp(34px,4.3vw,50px);
      }
      .createEventHeader p{
        margin-top:7px;
        font-size:8px;
        line-height:1.4;
      }
      .draftBadge{
        min-height:32px;
        padding:0 9px;
        border-radius:9px;
        font-size:7px;
      }

      .eventAgentBanner{
        margin-bottom:8px;
        padding:9px;
        border-radius:12px;
      }

      .eventCompletion{
        margin-bottom:8px;
        padding:9px 11px;
        border-radius:12px;
      }
      .eventCompletionTop{font-size:7px}
      .eventCompletion p{
        margin-top:5px;
        font-size:6.5px;
      }

      .createEventForm{
        grid-template-columns:repeat(2,minmax(0,1fr));
        gap:8px;
      }

      .eventFormSection{
        padding:12px;
        border-radius:15px;
      }

      .eventFormSection:first-of-type,
      .eventFormSection:last-of-type{
        grid-column:1 / -1;
      }

      .eventFormHeading{
        gap:8px;
        margin-bottom:9px;
      }
      .eventFormHeading>span{
        width:32px;
        height:32px;
        border-radius:9px;
      }
      .eventFormHeading small{font-size:5.5px}
      .eventFormHeading h2{
        margin-top:3px;
        font-size:16px;
      }
      .eventFormHeading p{
        margin-top:3px;
        font-size:6.5px;
        line-height:1.35;
      }

      .eventFieldsGrid{gap:7px}
      .createEventField{gap:4px}
      .createEventLabel{font-size:7px}
      .createEventInputIcon,
      .createEventTextareaWrapper>span{left:10px}
      .createEventTextareaWrapper>span{top:11px}
      .createEventInputWrapper input{
        min-height:38px;
        padding:0 10px 0 34px;
        border-radius:10px;
        font-size:8px;
      }
      .createEventTextareaWrapper textarea{
        min-height:92px;
        padding:10px 10px 10px 34px;
        border-radius:10px;
        font-size:8px;
        line-height:1.45;
        resize:none;
      }
      .createEventHint{font-size:6px}
      .descriptionCount{
        right:8px;
        bottom:7px;
        padding:3px 5px;
        font-size:5.5px;
      }

      .eventActivityPicker{
        margin-bottom:8px;
        padding:8px;
        border:1px solid #dce4d9;
        border-radius:11px;
        background:#f8faf6;
      }
      .eventActivityPickerTop{
        display:flex;
        align-items:center;
        justify-content:space-between;
        gap:8px;
        margin-bottom:6px;
      }
      .eventActivityPickerTop>span{
        color:#495c50;
        font-size:7px;
        font-weight:900;
      }
      .eventActivityPickerTop>small{
        color:#8a968e;
        font-size:6px;
      }
      .eventActivityGrid{
        display:grid;
        grid-template-columns:repeat(5,minmax(0,1fr));
        gap:4px;
        max-height:116px;
        overflow:auto;
        padding-right:2px;
      }
      .eventActivityGrid button{
        display:flex;
        align-items:center;
        gap:5px;
        min-height:29px;
        padding:0 7px;
        border:1px solid #dbe3d8;
        border-radius:8px;
        background:white;
        color:#506259;
        cursor:pointer;
        font-size:6.5px;
        font-weight:800;
        text-align:left;
      }
      .eventActivityGrid button:hover{
        border-color:#9db996;
        background:#f4f8f0;
      }
      .eventActivityGrid button.active{
        border-color:#75986b;
        background:#e9f3e4;
        color:#355a36;
        box-shadow:0 0 0 2px rgba(117,152,107,.08);
      }
      .eventActivityDot{
        flex:0 0 auto;
        width:6px;
        height:6px;
        border-radius:50%;
        background:#b8c6b3;
      }
      .eventActivityGrid button.active .eventActivityDot{
        background:#5c8755;
        box-shadow:0 0 0 3px rgba(92,135,85,.10);
      }

      .eventUpload{
        min-height:66px;
        gap:8px;
        padding:9px;
        border-radius:11px;
      }
      .eventUploadIcon{
        width:36px;
        height:36px;
        border-radius:10px;
      }
      .eventUploadCopy strong{font-size:8px}
      .eventUploadCopy small{
        margin-top:2px;
        font-size:6px;
        line-height:1.3;
      }
      .eventUploadAction{
        padding:6px 8px;
        border-radius:8px;
        font-size:6.5px;
      }
      .removeEventCover{
        margin-top:6px;
        padding:6px 8px;
        font-size:6.5px;
      }

      .createEventError{
        grid-column:1 / -1;
        gap:7px;
        padding:9px;
        border-radius:11px;
      }
      .createEventError>span{
        width:28px;
        height:28px;
      }
      .createEventError p{font-size:7.5px}

      .createEventActions{
        grid-column:1 / -1;
        gap:7px;
      }
      .cancelEventButton,
      .publishEventButton{
        min-height:40px;
        padding:0 13px;
        border-radius:10px;
        font-size:8px;
      }
      .publishEventButton{min-width:160px}

      .eventIncludedPresetGrid{
        display:grid;
        grid-template-columns:repeat(5,minmax(0,1fr));
        gap:8px;
      }
      .eventIncludedPresetGrid button{
        display:flex;align-items:center;gap:7px;justify-content:center;
        min-height:40px;padding:8px 9px;border-radius:12px;
        border:1px solid rgba(26,57,40,.13);background:#fff;color:#254333;
        font:inherit;font-size:9px;font-weight:800;cursor:pointer;
      }
      .eventIncludedPresetGrid button span{font-size:10px;opacity:.65}
      .eventIncludedPresetGrid button.active{
        background:#1f4d35;color:#fff;border-color:#1f4d35;
      }
      .eventIncludedCustomRow{
        display:grid;grid-template-columns:minmax(0,1fr) auto;gap:8px;margin-top:10px;
      }
      .eventIncludedCustomRow input{
        width:100%;min-height:42px;border:1px solid rgba(26,57,40,.14);
        border-radius:12px;padding:0 12px;background:#fff;color:#183326;
        font:inherit;font-size:11px;outline:none;
      }
      .eventIncludedCustomRow button{
        border:0;border-radius:12px;padding:0 16px;background:#244d36;color:#fff;
        font:inherit;font-size:10px;font-weight:800;cursor:pointer;
      }
      .eventIncludedSelected{
        display:flex;flex-wrap:wrap;gap:7px;margin-top:10px;
      }
      .eventIncludedSelected>span{
        display:inline-flex;align-items:center;gap:7px;
        padding:7px 9px;border-radius:999px;background:#edf4ef;color:#244d36;
        font-size:9px;font-weight:800;
      }
      .eventIncludedSelected button{
        border:0;background:transparent;color:inherit;font-size:15px;line-height:1;
        padding:0;cursor:pointer;
      }
      @media(max-width:700px){
        .eventIncludedPresetGrid{grid-template-columns:repeat(2,minmax(0,1fr))}
        .eventIncludedCustomRow{grid-template-columns:1fr}
        .eventIncludedCustomRow button{min-height:40px}
      }

      .eventPhotoGrid{
        display:grid;
        grid-template-columns:repeat(4,minmax(0,1fr));
        gap:10px;
        margin-top:12px;
      }
      .eventPhotoItem{
        position:relative;
        overflow:hidden;
        border:1px solid rgba(21,52,36,.12);
        border-radius:14px;
        background:#eef2ec;
        aspect-ratio:4/3;
      }
      .eventPhotoItem.cover{
        box-shadow:0 0 0 2px #315f43 inset;
      }
      .eventPhotoItem img{
        width:100%;height:100%;object-fit:cover;display:block;
      }
      .eventPhotoCoverBadge{
        position:absolute;left:8px;top:8px;
        padding:5px 8px;border-radius:999px;
        background:rgba(14,35,24,.88);color:#fff;
        font-size:9px;font-weight:800;
      }
      .eventPhotoActions{
        position:absolute;left:6px;right:6px;bottom:6px;
        display:flex;gap:6px;justify-content:flex-end;
      }
      .eventPhotoActions button{
        border:0;border-radius:9px;padding:7px 9px;
        background:rgba(255,255,255,.94);color:#183326;
        font-size:9px;font-weight:800;cursor:pointer;
      }
      .eventPhotoActions button.danger{
        display:grid;place-items:center;padding:7px;color:#8d2f2f;
      }
      @media(max-width:640px){
        .eventPhotoGrid{grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}
        .eventPhotoActions button{font-size:10px;padding:8px}
      }

      .eventSecurityNotice{
        grid-column:1 / -1;
        font-size:6.5px;
      }

      @media(max-width:1050px){
        .createEventForm{grid-template-columns:1fr}
        .eventFormSection:first-of-type,
        .eventFormSection:last-of-type{grid-column:auto}
        .createEventError,.createEventActions,.eventSecurityNotice{grid-column:auto}
      }

      @media(max-width:850px){
        .createEventPage{padding:10px}
        .eventPreviewPanel{
          position:static;
          max-height:none;
        }
        .eventPreviewImage{height:240px}
      }

      @media(max-width:680px){
        .createEventPage{padding:0}
        .createEventContent{padding:14px 9px 28px}
        .createEventHeader{
          margin-bottom:8px;
          flex-direction:row;
          align-items:flex-start;
        }
        .mobileBackButton{
          width:32px;
          height:32px;
          margin-bottom:8px;
        }
        .createEventBrand{margin-bottom:7px}
        .createEventHeader h1{font-size:32px}
        .createEventHeader p{display:none}
        .draftBadge{
          min-height:28px;
          padding:0 7px;
        }
        .eventCompletion{padding:7px 8px}
        .eventCompletion p{display:none}

        .createEventForm{gap:6px}
        .eventFormSection{padding:9px;border-radius:12px}
        .eventFormHeading{
          margin-bottom:7px;
        }
        .eventFormHeading>span{
          width:28px;
          height:28px;
        }
        .eventFormHeading h2{font-size:14px}
        .eventFormHeading p{display:none}

        .eventFieldsGrid{
          grid-template-columns:repeat(2,minmax(0,1fr));
          gap:5px;
        }

        .eventActivityGrid{
          grid-template-columns:repeat(2,minmax(0,1fr));
          max-height:154px;
          gap:3px;
        }
        .eventActivityGrid button{
          min-height:27px;
          padding:0 6px;
          font-size:6px;
        }
        .eventActivityPickerTop>small{display:none}

        .createEventInputWrapper input{min-height:36px}
        .createEventTextareaWrapper textarea{min-height:78px}
        .eventUpload{min-height:58px}

        .createEventActions{
          position:sticky;
          z-index:5;
          bottom:6px;
          padding:6px;
          border:1px solid #dbe4d8;
          border-radius:12px;
          background:rgba(250,251,247,.94);
          backdrop-filter:blur(12px);
          box-shadow:0 10px 28px rgba(31,51,38,.10);
        }
        .cancelEventButton{flex:.55}
        .publishEventButton{
          flex:1;
          min-width:0;
        }
      }

      @media(max-width:420px){
        .eventFieldsGrid{grid-template-columns:1fr}
        .createEventHeader h1{font-size:29px}
        .eventActivityGrid{grid-template-columns:repeat(2,minmax(0,1fr))}
      }


      @media (prefers-reduced-motion: reduce) {
        *,
        *::before,
        *::after {
          animation: none !important;
          scroll-behavior: auto !important;
          transition: none !important;
        }
      }
    `}</style>
  );
}